import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { CotizacionDeAlistamiento } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import { cantidad, cuantoHay, lineas, paraEscribir, sugerido } from './cantidades'
import { EstadoDeFev } from './EstadoDeFev'
import { usePasarAFev } from './usePasarAFev'
import { useSepararCotizacion } from './useSepararCotizacion'
import estilos from './PaginaAlistamientos.module.css'

/**
 * LA PLANILLA DE UNA COTIZACIÓN: CUÁNTO HAY Y QUÉ PASA A FEV (0252).
 *
 * Johan: «"No hay" significa que no hay x cantidad, pero se puede facturar la
 * cantidad que haya, y la cantidad que no haya pasa a una COT nueva; por
 * ende debes también mostrar cantidades».
 *
 * Cada material dice lo pedido y lo que hay en bodega, y bodega escribe
 * cuánto HAY (arranca con lo que dice el inventario de World Office). Lo que
 * falta se calcula solo. Arriba, en vivo, lo que se queda y lo que nace.
 *
 * Al final, un solo paso según el caso:
 *   · Todo completo y aceptada → «Pasar a FEV».
 *   · Falta algo y aceptada    → «Separar y pasar lo que hay a FEV».
 *   · Falta algo, sin aceptar  → «Separar»: la FEV espera al cliente.
 */
export function PlanillaDeCotizacion({
  cotizacion: c,
  ordenServicioId,
  onHecho,
}: {
  cotizacion: CotizacionDeAlistamiento
  /** Para mandar a aprobarla cuando todavía no está en World Office. */
  ordenServicioId: string
  /** Lo que pasó, para avisarlo arriba aunque la cotización cambie de pila. */
  onHecho: (texto: string) => void
}) {
  const separar = useSepararCotizacion()
  const pasar = usePasarAFev()
  const [escrito, setEscrito] = useState<Record<number, string>>({})
  const [confirmando, setConfirmando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ls = lineas(c.renglones, escrito)
  const faltan = ls.filter((l) => l.falta > 0)
  const sinNada = ls.length > 0 && ls.every((l) => l.hay <= 0)
  // Solo servicio (la grúa, una visita): no hay nada que alistar, pero sí que
  // pasar a FEV. Antes se decía «nada que alistar» y no había botón: se quedaba
  // en «Para alistar» para siempre (Johan, con la FEV 3049).
  const sinMaterial = ls.length === 0
  const valorSale = faltan.reduce((s, l) => s + l.valorFalta, 0)
  const valorQueda = Math.max(Number(c.valor ?? 0) - valorSale, 0)
  const aceptada = c.estado === 'aprobada'
  const ocupado = separar.isPending || pasar.isPending
  // 0296: lo que se decidió en la cotización. Mismo paso, otro documento.
  const remision = c.documento === 'remision'
  const doc = remision ? 'remisión' : 'FEV'

  // Lo que no deja tocar nada, dicho en una frase. Una COT armada en la
  // app y sin aprobar NO va en camino a World Office: allá llega cuando
  // gerencia le da «Aprobar y enviar al cliente». Decía «espera a que
  // llegue» y Johan preguntó qué significaba.
  const sinAprobar = !c.en_world_office && !c.en_camino
  // Sin IVA (0272): sigue el mismo camino, pero su FEV es solo en la app.
  // Sin IVA pesa solo para la FEV; una remisión va igual a World Office.
  const sinIva = Boolean(c.sin_iva) && !remision
  const bloqueo = c.fev
    ? null // se muestra su estado de FEV
    : c.facturada
    ? 'Ya pasó a factura.'
    : sinAprobar
    ? 'Se armó en la app y todavía no está en World Office: llega allá cuando gerencia la apruebe y se la envíe al cliente. Hasta entonces no se puede separar ni pasar a FEV, porque las dos cosas cambian su documento en World Office.'
    : !c.en_world_office
    ? 'Va en camino a World Office: su número llega en un momento.'
    : c.en_camino
    ? 'Tiene un cambio en camino a World Office: espera a que llegue.'
    : null
  const quieto = Boolean(c.fev) || Boolean(bloqueo) || ocupado

  function escribir(id: number, texto: string) {
    setEscrito((a) => ({ ...a, [id]: texto }))
    setConfirmando(false)
  }

  function todos(cual: 'inventario' | 'completo') {
    setEscrito(
      cual === 'inventario'
        ? {}
        : Object.fromEntries(ls.map((l) => [l.id, paraEscribir(l.pedido)])),
    )
    setConfirmando(false)
  }

  async function confirmar() {
    setError(null)
    try {
      if (faltan.length === 0) {
        const r = await pasar.mutateAsync(c.cotizacion_id)
        onHecho(
          r === 'partida_por_iva'
            ? `${c.numero} se partió en dos: se queda con lo que lleva el IVA de World Office (FEV) y lo que tiene el IVA cambiado pasó a una COT nueva que va a remisión. Cuando World Office confirme, pase cada una.`
            : r === 'remision_en_espera'
            ? `${c.numero} quedó lista para remisión. Queda en espera: la remisión desde la app todavía no está encendida en World Office.`
            : r === 'remision_en_cola'
            ? `${c.numero} quedó lista para remisión: está en la cola de World Office.`
            : r === 'sin_iva'
            ? `${c.numero} pasó a FEV solo en la app: no lleva IVA, World Office no la factura.`
            : r === 'en_cola'
            ? `${c.numero} quedó lista para FEV: está en la cola de facturación.`
            : `${c.numero} quedó lista para FEV. Entra a la cola en cuanto World Office confirme su último cambio.`,
        )
      } else {
        await separar.mutateAsync({
          cotizacionId: c.cotizacion_id,
          hay: cuantoHay(ls),
          facturar: aceptada,
        })
        onHecho(
          aceptada
            ? `${c.numero} se quedó con lo que hay y quedó lista para ${doc}${
                sinIva ? ' (solo en la app: no lleva IVA)' : ''
              }. Lo que falta pasó a una COT nueva de la misma orden${
                sinIva ? ', también sin IVA' : ''
              }; su número llega de World Office en un momento.`
            : `${c.numero} se quedó con lo que hay. Lo que falta pasó a una COT nueva de la misma orden. La ${doc} espera a que el cliente acepte.`,
        )
      }
      setConfirmando(false)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo.')
    }
  }

  return (
    <div className={estilos.planilla}>
      {c.fev && <EstadoDeFev fev={c.fev} cotizacionId={c.cotizacion_id} />}
      {/* 0300: al pasarla se parte en dos por el IVA. */}
      {c.partir_por_iva && !c.fev && (
        <div className={estilos.fev}>
          <p className={estilos.fevTitulo}>Se parte en dos: FEV y remisión</p>
          <p>
            Tiene renglones con el IVA cambiado frente a World Office. Al pasarla, lo que lleva el
            IVA de World Office se queda aquí como FEV y lo demás pasa a una COT nueva que va a
            remisión.
          </p>
        </div>
      )}
      {remision && !c.fev && (
        <div className={estilos.fev}>
          <p className={estilos.fevTitulo}>Se vuelve remisión</p>
          <p>
            Así se decidió en la cotización: al alistarla no pasa a FEV, pasa a remisión. Lo que
            falte sale en una COT nueva, igual que siempre.
          </p>
        </div>
      )}
      {sinIva && !c.fev && !c.facturada && (
        <div className={estilos.fev}>
          <p className={estilos.fevTitulo}>Sin IVA</p>
          <p>
            Se separa y se pasa a FEV como cualquier otra, pero su FEV es solo en la app: World
            Office no la factura y se cobra con la cotización. Lo que falte sale en una COT nueva,
            también sin IVA.
          </p>
        </div>
      )}

      {/* ── Lo que se queda y lo que nace, en vivo ───────────────────────── */}
      {!c.fev && !sinMaterial && (
        <div className={estilos.resultado}>
          <div className={estilos.lado}>
            <p className={estilos.ladoRotulo}>
              {aceptada ? `Pasa a ${doc}` : 'Se queda'} en {c.numero}
            </p>
            <p className={estilos.ladoCifra}>{pesos(valorQueda)}</p>
            <p className={estilos.ladoValor}>
              {ls.length - faltan.length} completos
              {faltan.length > 0 && ` · ${faltan.filter((l) => l.hay > 0).length} en parte`}
              {' · '}con su mano de obra
            </p>
          </div>
          <span className={estilos.flechaSeparar} aria-hidden="true">
            ⇄
          </span>
          <div
            className={`${estilos.lado} ${faltan.length ? estilos.ladoNuevo : estilos.ladoVacio}`}
          >
            <p className={estilos.ladoRotulo}>Nace una COT nueva</p>
            <p className={estilos.ladoCifra}>{faltan.length ? pesos(valorSale) : '—'}</p>
            <p className={estilos.ladoValor}>
              {faltan.length
                ? `${faltan.length} ${
                    faltan.length === 1 ? 'material' : 'materiales'
                  } · lo que falta`
                : 'no falta nada'}
            </p>
          </div>
        </div>
      )}

      {/* ── Los materiales ───────────────────────────────────────────────── */}
      {!c.fev && !sinMaterial && (
        <div className={estilos.herramientasPlanilla}>
          <span>Escribe cuánto hay en bodega. Arranca con lo que dice el inventario.</span>
          <button
            type="button"
            className={estilos.textoBoton}
            onClick={() => todos('inventario')}
            disabled={quieto}
          >
            Según inventario
          </button>
          <button
            type="button"
            className={estilos.textoBoton}
            onClick={() => todos('completo')}
            disabled={quieto}
          >
            Todo completo
          </button>
        </div>
      )}

      {sinMaterial ? (
        <p className={estilos.vacio}>
          No lleva material: nada que alistar.
          {!c.fev && aceptada ? ` Solo falta pasarla a ${doc}.` : ''}
        </p>
      ) : (
        <div className={estilos.materiales} role="table" aria-label={`Materiales de ${c.numero}`}>
          <div className={estilos.materialCabeza} role="row">
            <span role="columnheader">Material</span>
            <span role="columnheader">Pedido</span>
            <span role="columnheader">En bodega</span>
            <span role="columnheader">Hay</span>
            <span role="columnheader">Falta</span>
          </div>
          {ls.map((l) => {
            const unidad = l.renglon.unidad ?? 'Und.'
            const parcial = l.falta > 0 && l.hay > 0
            return (
              <div
                key={l.id}
                role="row"
                className={`${estilos.material} ${
                  l.falta > 0 ? (parcial ? estilos.materialParte : estilos.materialNoHay) : ''
                }`}
              >
                <span className={estilos.materialNombre} role="cell">
                  {l.renglon.descripcion}
                </span>
                <span className={estilos.materialNumero} role="cell" data-rotulo="Pedido">
                  {cantidad(l.pedido)} <small>{unidad}</small>
                </span>
                <span
                  className={`${estilos.materialNumero} ${
                    Number(l.renglon.existencia) < l.pedido ? estilos.materialCorto : ''
                  }`}
                  role="cell"
                  data-rotulo="Bodega"
                >
                  {cantidad(Number(l.renglon.existencia))}
                </span>
                <span className={estilos.materialHay} role="cell" data-rotulo="Hay">
                  {c.fev ? (
                    <b>{cantidad(l.pedido)}</b>
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={escrito[l.id] ?? paraEscribir(sugerido(l.renglon))}
                      onChange={(e) => escribir(l.id, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={quieto}
                      aria-label={`Cuánto hay de ${l.renglon.descripcion}`}
                    />
                  )}
                </span>
                <span className={estilos.materialFalta} role="cell" data-rotulo="Falta">
                  {l.falta > 0 && !c.fev ? (
                    <b>{cantidad(l.falta)}</b>
                  ) : (
                    <span className={estilos.materialOk}>✓</span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* ── El paso ──────────────────────────────────────────────────────── */}
      {error && <p className={estilos.error}>{error}</p>}
      {c.fev ? null : bloqueo ? (
        <p className={estilos.motivo}>
          {bloqueo}
          {sinAprobar && (
            <>
              {' '}
              <Link to={`/ordenes/${ordenServicioId}`} className={estilos.motivoEnlace}>
                Abrir la orden para aprobarla ›
              </Link>
            </>
          )}
        </p>
      ) : sinNada ? (
        <p className={estilos.motivo}>
          No hay nada de ningún material: no hay que separar. La cotización entera espera el
          material.
        </p>
      ) : faltan.length === 0 && !aceptada ? (
        <p className={estilos.motivo}>
          Está completa. Pasa a {doc} cuando el cliente la acepte: la {doc} solo se hace con la
          cotización aceptada.
        </p>
      ) : confirmando ? (
        <div className={estilos.confirmar}>
          {faltan.length === 0 ? (
            <p>
              <b>{c.numero}</b> pasa completa a {doc} por {pesos(Number(c.valor ?? 0))}.{' '}
              {remision
                ? 'Queda lista para remisión.'
                : sinIva
                ? 'No lleva IVA: queda en FEV solo en la app, World Office no la factura.'
                : 'Queda en la cola de facturación de World Office.'}
            </p>
          ) : (
            <>
              <p>
                <b>{c.numero}</b> se modifica en World Office y se queda con lo que hay, por{' '}
                {pesos(valorQueda)}
                {aceptada ? `, y queda lista para ${doc}` : ''}. Nace una cotización nueva
                {aceptada ? ', ya aceptada,' : ''} en la misma orden con lo que falta, por{' '}
                {pesos(valorSale)}:
              </p>
              <ul className={estilos.confirmarLista}>
                {faltan.map((l) => (
                  <li key={l.id}>
                    <b>
                      {cantidad(l.falta)} {l.renglon.unidad ?? 'Und.'}
                    </b>{' '}
                    {l.renglon.descripcion}
                    {l.hay > 0 && (
                      <small>
                        {' '}
                        (de {cantidad(l.pedido)}; se quedan {cantidad(l.hay)})
                      </small>
                    )}
                  </li>
                ))}
              </ul>
              <p className={estilos.confirmarNota}>No se crea otra orden de trabajo.</p>
            </>
          )}
          <div className={estilos.confirmarBotones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setConfirmando(false)}
            >
              Volver
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void confirmar()}
              disabled={ocupado}
            >
              {ocupado
                ? 'Un momento…'
                : faltan.length === 0
                ? `Sí, pasar a ${doc}`
                : 'Sí, separar'}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={estilos.separar} onClick={() => setConfirmando(true)}>
          {faltan.length === 0
            ? sinIva
              ? 'Listo: pasar a FEV (solo en la app)'
              : `Listo: pasar a ${doc}`
            : aceptada
            ? `Separar y pasar lo que hay a ${doc} · ${faltan.length} ${
                faltan.length === 1 ? 'material' : 'materiales'
              } a una COT nueva`
            : `Separar: lo que falta de ${faltan.length} ${
                faltan.length === 1 ? 'material' : 'materiales'
              } pasa a una COT nueva`}
        </button>
      )}
    </div>
  )
}
